/*
  cart-drawer.js - NOTFORTOMORROW OS2.0 - JS surface 3 (cart drawer + cart page)
  <cart-drawer> / <cart-items> web components. Loaded by sections/cart-drawer.liquid +
  sections/main-cart.liquid. Uses Shopify /cart/*.js ajax + the Section Rendering API
  (NOT a global fetch monkey-patch). The cart PAGE works with zero JS; the drawer + in-place
  qty/remove are progressive enhancements that re-render the shared 'cart-line-item' snippet.

  Behaviour: open/close drawer (via <nft-dialog>), qty stepper + change, remove line,
  free-ship progress bar + announced remaining-to-free-shipping, subtotal sync.
*/

const announce = (m, a) => { if (window.nft && typeof window.nft.announce === 'function') window.nft.announce(m, a); };

const SECTION_DRAWER = 'cart-drawer';
const SECTION_PAGE = 'main-cart';

/* Section Rendering API helper: returns the parsed cart + a section HTML map. */
async function postCart(url, body, sections) {
  const payload = Object.assign({}, body);
  if (sections && sections.length) {
    payload.sections = sections.join(',');
    payload.sections_url = window.location.pathname;
  }
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload)
  });
  return res.json();
}

/* Swap a [data-cart-items] region from a freshly-rendered section's HTML. */
function swapItems(scope, html) {
  if (!html) return;
  const fresh = new DOMParser().parseFromString(html, 'text/html');
  const newItems = fresh.querySelector('[data-cart-items]');
  if (!newItems && scope.classList && scope.classList.contains('main-cart')) {
    const newCart = fresh.querySelector('.main-cart');
    if (newCart) {
      scope.innerHTML = newCart.innerHTML;
      scope.className = newCart.className;
      scope.querySelectorAll('[data-reveal], [data-stagger]').forEach((el) => el.classList.add('in'));
      const title = scope.querySelector('.nfp-cart-empty__title');
      if (title) {
        title.setAttribute('tabindex', '-1');
        title.focus({ preventScroll: true });
      }
      return;
    }
  }
  const curItems = scope.querySelector('[data-cart-items]');
  if (newItems && curItems) curItems.innerHTML = newItems.innerHTML;
  const newAmounts = fresh.querySelector('[data-cart-amounts]');
  const curAmounts = scope.querySelector('[data-cart-amounts]');
  if (newAmounts && curAmounts) curAmounts.innerHTML = newAmounts.innerHTML;
  const textSelectors = ['[data-cart-count]'];
  if (scope.classList && scope.classList.contains('main-cart')) {
    textSelectors.push('[data-cart-sticky-label]', '[data-cart-sticky-subtotal]');
  }
  textSelectors.forEach((selector) => {
    const next = fresh.querySelector(selector);
    const current = scope.querySelector(selector);
    if (next && current) current.textContent = next.textContent;
  });
}

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('nft-dialog');
    document.addEventListener('cart:add-success', () => this.refreshAndOpen());
    // Keep the drawer in sync when the cart page (or any surface) changes the cart.
    document.addEventListener('cart:updated', (e) => {
      if (e.detail && e.detail.sections) this._applySections(e.detail.sections);
      this._updateProgressBar(e.detail);
    });
  }

  async refreshAndOpen() {
    await this.refresh();
    // refresh() may re-inject the drawer markup, so re-query the dialog (the cached ref can go stale).
    this.dialog = this.querySelector('nft-dialog') || this.dialog;
    if (this.dialog) this.dialog.open();
  }

  async refresh() {
    try {
      const res = await fetch(`/cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      // Render the drawer from the current page path. The page URL always carries the active
      // locale, while Shopify.routes.root was measured as "/" on localized pages on
      // 2026-09-28, which made the drawer come back in English.
      const secRes = await fetch(`${window.location.pathname}?sections=${SECTION_DRAWER}`);
      const sectionsMap = await secRes.json();
      cart.sections = sectionsMap;
      this._applySections(sectionsMap);
      this._updateProgressBar(cart);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    } catch (_) { /* keep current DOM; native cart page still works */ }
  }

  _applySections(sectionsMap) {
    const html = sectionsMap && sectionsMap[SECTION_DRAWER];
    if (!html) return;
    const fresh = new DOMParser().parseFromString(html, 'text/html');
    const freshPanel = fresh.querySelector('.cart-drawer__panel');
    const curPanel = this.querySelector('.cart-drawer__panel');
    if (!freshPanel || !curPanel) { swapItems(this, html); return; }
    // Rebuild every panel child EXCEPT the header. The <nft-dialog> primitive binds the
    // close button with a direct listener at connect, so the header must persist. Rebuilding
    // the rest in one pass syncs the free-ship / reward bar, the line items, the subtotal AND
    // the checkout footer together - so the very first add from an empty cart still renders a
    // checkout button + reward bar (previously only the line list was swapped).
    [...curPanel.children].forEach((c) => { if (!c.classList.contains('cart-drawer__head')) c.remove(); });
    [...freshPanel.children].forEach((c) => { if (!c.classList.contains('cart-drawer__head')) curPanel.appendChild(c.cloneNode(true)); });
  }

  _updateProgressBar(cart) {
    const bar = this.querySelector('[data-ship-progress]');
    if (!bar || !cart) return;
    const threshold = parseFloat(bar.dataset.threshold || '0') * 100; // cents
    if (threshold <= 0) return;
    const total = cart.total_price || 0;
    const pct = Math.min(100, (total / threshold) * 100);
    bar.style.setProperty('--progress', pct + '%');
    const track = bar.querySelector('[role="progressbar"]');
    if (track) track.setAttribute('aria-valuenow', Math.round(pct));
    const text = bar.querySelector('[data-ship-text]');
    if (!text) return;
    const remaining = Math.max(0, threshold - total);
    if (remaining <= 0) {
      text.textContent = bar.dataset.msgReached || text.textContent;
    } else if (bar.dataset.msgProgress) {
      const money = window.Shopify && window.Shopify.formatMoney
        ? window.Shopify.formatMoney(remaining)
        : (remaining / 100).toFixed(2);
      text.textContent = bar.dataset.msgProgress.replace('__AMOUNT__', money);
    }
  }
}
if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

class CartItems extends HTMLElement {
  connectedCallback() {
    // Section this <cart-items> belongs to (drawer vs page) for the rendering API.
    this.section = this.closest('cart-drawer') ? SECTION_DRAWER : SECTION_PAGE;
    this._debounce = null;

    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-qty-input]')) this._changeQty(e.target);
    });
    this.addEventListener('click', (e) => {
      const step = e.target.closest('[data-qty-step]');
      if (step) { e.preventDefault(); this._step(step); return; }
      const remove = e.target.closest('[data-remove-line]');
      if (remove) { e.preventDefault(); this._removeLine(remove); }
    });

    // STALE-LINES (incident 2026-08-11): reward-bar.js (free gift) and bundle-add.js
    // write to the cart directly and announce it with 'cart:refresh'. Nothing rendered
    // this list on that event, so the DOM kept its old 1-based line numbers while
    // /cart.js had already shifted. Remove and qty address lines by NUMBER, so a
    // customer clicking remove on their own item deleted the free bottle instead.
    // Re-render on that event and the numbers are correct again by construction.
    document.addEventListener('cart:refresh', () => this._refresh());
  }

  /* Re-render this list from the section rendering API. Read-only: never dispatches. */
  async _refresh() {
    try {
      // NO Accept:application/json here. On /cart that header makes Shopify answer
      // with the CART object instead of the rendered section, so the swap silently
      // did nothing. Plain fetch returns { "<section-id>": "<html>" }.
      const res = await fetch(window.location.pathname + '?sections=' + this.section);
      if (!res.ok) return;
      const data = await res.json();
      const html = data && data[this.section];
      if (html) swapItems(this.closest('cart-drawer') || this.closest('.main-cart') || document, html);
    } catch (_) { /* leave the DOM as-is rather than blanking the cart */ }
  }

  _step(btn) {
    const wrap = btn.closest('[data-qty-wrap]');
    const input = wrap && wrap.querySelector('[data-qty-input]');
    if (!input) return;
    const next = Math.max(0, parseInt(input.value, 10) + parseInt(btn.dataset.qtyStep, 10));
    input.value = next;
    this._changeQty(input);
  }

  _changeQty(input) {
    // Debounce rapid stepper clicks into one request.
    clearTimeout(this._debounce);
    const line = parseInt(input.dataset.line, 10);
    const quantity = Math.max(0, parseInt(input.value, 10) || 0);
    this._debounce = setTimeout(() => {
      this._postChange({ line, quantity });
      if (quantity === 0 && input.dataset.removedMessage) announce(input.dataset.removedMessage, true);
    }, 300);
  }

  async _removeLine(btn) {
    const line = parseInt(btn.dataset.removeLine, 10);
    await this._postChange({ line, quantity: 0 });
    announce(btn.dataset.removedMessage || 'Item removed', true);
  }

  async _postChange(body) {
    try {
      const sections = [this.section];
      const cart = await postCart('/cart/change.js', body, sections);
      this._applyLocal(cart);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    } catch (_) { window.location.href = '/cart'; } // fallback to native cart page
  }

  /* Repaint this region (and its subtotal) from the section HTML in the change payload. */
  _applyLocal(cart) {
    const scope = this.closest('cart-drawer') || this.closest('.main-cart') || document;
    if (cart && cart.sections && cart.sections[this.section]) {
      swapItems(scope, cart.sections[this.section]);
    }
  }
}
if (!customElements.get('cart-items')) customElements.define('cart-items', CartItems);
